import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";

const MODE_CARDS = [
  {
    key: "add",
    title: "Add Gage",
    description: "Create a new gage entry in the system.",
    icon: "ri-add-circle-line",
    tone: "primary",
  },
  {
    key: "modify",
    title: "Modify Gage",
    description: "Rename an existing gage while keeping the workflow simple.",
    icon: "ri-edit-2-line",
    tone: "warning",
  },
  {
    key: "delete",
    title: "Delete Gage",
    description: "Remove a gage entry after confirmation.",
    icon: "ri-delete-bin-6-line",
    tone: "danger",
  },
];

export default function Gages({ onBack }) {
  const dialog = useAppDialog();

  const [mode, setMode] = useState("");
  const [activeMode, setActiveMode] = useState("");

  const [gageList, setGageList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [hasLoadedGageList, setHasLoadedGageList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");

  const [addGageName, setAddGageName] = useState("");
  const [selectedGage, setSelectedGage] = useState("");
  const [newGageName, setNewGageName] = useState("");

  // Gages round (Issue 4): the backend has always exposed the GaugeLocator
  // read endpoints (api/mafia/gauge-locator, api/mafia/unique-gauge-types)
  // but nothing in the frontend called them, so the page showed no data
  // until an Add/Modify/Delete mode was picked. This panel loads the gauge
  // locator records on open (read-only browse: AssetNo / Description /
  // Type / Location / GO / NG / Uses), with a text search and a Type filter
  // fed by unique-gauge-types. Local pluto_SSWDASH GaugeLocator table only;
  // no schema change; endpoints unchanged.
  const [locatorRows, setLocatorRows] = useState([]);
  const [locatorTypes, setLocatorTypes] = useState([]);
  const [locatorLoading, setLocatorLoading] = useState(false);
  const [locatorError, setLocatorError] = useState("");
  const [locatorSearch, setLocatorSearch] = useState("");
  const [locatorType, setLocatorType] = useState("");

  const resetFormState = useCallback(() => {
    setMode("");
    setActiveMode("");
    setAddGageName("");
    setSelectedGage("");
    setNewGageName("");
    setPageError("");
  }, []);

  const loadGageList = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && hasLoadedGageList) {
        return;
      }

      try {
        setLoadingList(true);
        setPageError("");
        const data = await getGageList();
        setGageList(data || []);
        setHasLoadedGageList(true);
      } catch (error) {
        console.error("Failed to load gage list:", error);
        const message = extractApiError(error, "Failed to load gage list.");
        setPageError(message);
        await dialog.error(message);
      } finally {
        setLoadingList(false);
      }
    },
    [hasLoadedGageList, dialog]
  );

  useEffect(() => {
    if (activeMode === "modify" || activeMode === "delete") {
      loadGageList(false);
    }
  }, [activeMode, loadGageList]);

  // Gages round (Issue 4): load data as soon as the page opens instead of
  // waiting for a mode pick — the gage name list (feeds the Queue Size
  // badge and the Modify/Delete selects) plus the Gauge Locator browse.
  const loadLocator = useCallback(async () => {
    try {
      setLocatorLoading(true);
      setLocatorError("");
      const [rows, types] = await Promise.all([
        getGaugeLocator(),
        getUniqueGaugeTypes(),
      ]);
      setLocatorRows(Array.isArray(rows) ? rows : []);
      const typeValues = (Array.isArray(types) ? types : [])
        .map((row) => String(Object.values(row || {})[0] ?? "").trim())
        .filter(Boolean);
      setLocatorTypes(typeValues);
    } catch (error) {
      console.error("Failed to load gauge locator:", error);
      setLocatorError(
        extractApiError(error, "Failed to load gauge locator records.")
      );
    } finally {
      setLocatorLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGageList(false);
    loadLocator();
    
  }, []);

  const filteredLocatorRows = useMemo(() => {
    const search = locatorSearch.trim().toUpperCase();
    const type = locatorType.trim().toUpperCase();
    return locatorRows.filter((row) => {
      if (type) {
        const rowType = String(row?.Type ?? row?.type ?? "").trim().toUpperCase();
        if (rowType !== type) return false;
      }
      if (!search) return true;
      return Object.values(row || {}).some((value) =>
        String(value ?? "").toUpperCase().includes(search)
      );
    });
  }, [locatorRows, locatorSearch, locatorType]);

  // Round 60 (UX): handleContinue removed — clicking a mode card now
  // activates the workflow directly (no extra Continue step).

  async function handleAddGage() {
    if (!addGageName.trim()) {
      await dialog.warning("Please enter a gage name.");
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await addGage({ gage: addGageName.trim() });

      if (success === true) {
        await dialog.success("Gage Successfully Added");
        await loadGageList(true);
        resetFormState();
      } else {
        await dialog.error("Error in adding Gage");
      }
    } catch (error) {
      console.error("Add gage failed:", error);
      // Controlled error: prefer the backend's message (e.g. the 403
      // "Insufficient Mafia Aux permission for this action.") over a
      // generic string, so unauthorized users see why it was refused.
      const message = extractApiError(error, "Error in adding Gage");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleModifyGage() {
    if (selectedGage.trim() === "") {
      await dialog.warning("Please select a gage to modify");
      return;
    }

    if (newGageName.trim() === "") {
      await dialog.warning("Please enter the new name of the gage");
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await modifyGage({
        oldGage: selectedGage.trim(),
        newGage: newGageName.trim(),
      });

      if (success === false) {
        await dialog.error("Could not modify gage.");
      } else {
        await dialog.success("Gage successfully modified.");
        await loadGageList(true);
        resetFormState();
      }
    } catch (error) {
      console.error("Modify gage failed:", error);
      const message = extractApiError(error, "Could not modify gage.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteGage() {
    if (selectedGage.trim() === "") {
      await dialog.warning("Please select a gage to delete");
      return;
    }

    const confirmed = await dialog.confirm({
      title: "Delete Gage",
      message: `Are you sure you wish to delete ${selectedGage.trim()}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "error",
    });

    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await deleteGage({ gage: selectedGage.trim() });

      if (success === false) {
        await dialog.error("Could not delete gage.");
      } else {
        await dialog.success("Gage successfully deleted.");
        await loadGageList(true);
        resetFormState();
      }
    } catch (error) {
      console.error("Delete gage failed:", error);
      const message = extractApiError(error, "Could not delete gage.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  const activeModeMeta = useMemo(
    () => MODE_CARDS.find((item) => item.key === activeMode) || null,
    [activeMode]
  );

  return (
    <div className="gages-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-ruler-2-line"
        eyebrow="MAFIA Aux"
        title="Gages"
        subtitle="Add, modify, or remove gages used in MAFIA inspection workflows."
        tone="teal"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Gages" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="gages-toolbar-main">
                <div className="gages-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>
              </div>

              <div className="gages-toolbar-utility">
                {activeMode && (
                  <Button
                    color="light"
                    className="gages-action-btn gages-utility-btn border"
                    onClick={resetFormState}
                    disabled={submitting}
                  >
                    <i className="ri-refresh-line gages-btn-icon me-1" />
                    Reset
                  </Button>
                )}

                {(activeMode === "modify" || activeMode === "delete") && (
                  <Button
                    color="secondary"
                    className="gages-action-btn gages-utility-btn"
                    onClick={() => loadGageList(true)}
                    disabled={loadingList || submitting}
                  >
                    <i className="ri-refresh-line gages-btn-icon me-1" />
                    {loadingList ? "Refreshing..." : "Refresh List"}
                  </Button>
                )}

                <Badge color="light" className="gages-info-badge">
                  Queue Size: {gageList.length}
                </Badge>

                <div className="gages-info-pill">
                  <span className="gages-info-label">Selected Action</span>
                  <span className="gages-info-value">
                    {mode ? toTitle(mode) : "None"}
                  </span>
                </div>

                <div className="gages-info-pill">
                  <span className="gages-info-label">Active Workflow</span>
                  <span className="gages-info-value">
                    {activeMode ? toTitle(activeMode) : "Waiting"}
                  </span>
                </div>

                <div className="gages-info-pill">
                  <span className="gages-info-label">List State</span>
                  <span className="gages-info-value">
                    {loadingList ? "Loading" : hasLoadedGageList ? "Cached" : "Not Loaded"}
                  </span>
                </div>

                <div className="gages-refresh-note">
                  {activeModeMeta ? activeModeMeta.title : "Choose an action to continue."}
                </div>
              </div>

              {pageError ? (
                <div className="gages-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <div className="gages-mode-grid mb-4">
                {MODE_CARDS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`gages-mode-card gages-mode-card-${item.tone} ${mode === item.key ? "active" : ""}`}
                    onClick={() => {
                      // Round 60 (UX): one click activates the workflow —
                      // the old two-step "pick a card, then press Continue"
                      // was an unnecessary extra step.
                      setMode(item.key);
                      setActiveMode(item.key);
                      setSelectedGage("");
                      setNewGageName("");
                      setPageError("");
                    }}
                  >
                    <div className="gages-mode-icon">
                      <i className={item.icon} />
                    </div>
                    <div className="gages-mode-content">
                      <div className="gages-mode-title">{item.title}</div>
                      <div className="gages-mode-copy">{item.description}</div>
                    </div>
                  </button>
                ))}
              </div>

              {activeMode === "add" && (
                <div className="gages-panel-card">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div>
                      <h5 className="mb-1">Add Gage</h5>
                      <p className="text-muted mb-0">Create a new gage entry in the system.</p>
                    </div>
                    <Badge color="light" className="gages-soft-badge text-uppercase">Create</Badge>
                  </div>

                  <Row className="g-3 align-items-end">
                    <Col xl={8}>
                      <Label className="form-label">Gage Name</Label>
                      <Input
                        type="text"
                        value={addGageName}
                        onChange={(e) => setAddGageName(e.target.value)}
                        onKeyDown={(e) => {
                          // Round 61 (UX): Enter submits, like every form.
                          if (e.key === "Enter" && !submitting) handleAddGage();
                        }}
                        placeholder="Enter a new gage name"
                      />
                    </Col>
                    <Col xl={4}>
                      <Button
                        color="success"
                        className="w-100 gages-action-btn"
                        onClick={handleAddGage}
                        disabled={submitting}
                      >
                        <i className="ri-add-circle-line gages-btn-icon me-1" />
                        {submitting ? "Adding..." : "Add Gage"}
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}

              {activeMode === "modify" && (
                <div className="gages-panel-card">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div>
                      <h5 className="mb-1">Modify Gage</h5>
                      <p className="text-muted mb-0">Select an existing gage and update its name.</p>
                    </div>
                    <Badge color="light" className="gages-soft-badge text-uppercase">Update</Badge>
                  </div>

                  {loadingList ? (
                    <div className="text-muted">Loading gage list...</div>
                  ) : gageList.length === 0 ? (
                    <div className="text-muted">
                      <i className="ri-information-line me-1" />
                      No gages exist in the dashboard database yet. If the
                      legacy application shows gages, the MafiaGage data has
                      not been copied into pluto_SSWDASH — run
                      sql/Round60_Seed_MafiaGage_GaugeLocator.sql, then
                      Refresh List. You can also create one via Add Gage.
                    </div>
                  ) : (
                    <Row className="g-3 align-items-end">
                      <Col xl={5}>
                        <Label className="form-label">Select Gage</Label>
                        <Input
                          type="select"
                          value={selectedGage}
                          onChange={(e) => setSelectedGage(e.target.value)}
                        >
                          <option value="">-- Select Gage --</option>
                          {gageList.map((gage) => (
                            <option key={gage} value={gage}>
                              {gage}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col xl={5}>
                        <Label className="form-label">New Gage Name</Label>
                        <Input
                          type="text"
                          value={newGageName}
                          onChange={(e) => setNewGageName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !submitting) handleModifyGage();
                          }}
                          placeholder="Enter updated gage name"
                        />
                      </Col>
                      <Col xl={2}>
                        <Button
                          color="warning"
                          className="w-100 gages-action-btn text-white"
                          onClick={handleModifyGage}
                          disabled={submitting}
                        >
                          <i className="ri-edit-2-line gages-btn-icon me-1" />
                          {submitting ? "Updating..." : "Modify"}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {activeMode === "delete" && (
                <div className="gages-panel-card">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div>
                      <h5 className="mb-1">Delete Gage</h5>
                      <p className="text-muted mb-0">Select a gage and remove it after confirmation.</p>
                    </div>
                    <Badge color="light" className="gages-soft-badge text-uppercase">Delete</Badge>
                  </div>

                  {loadingList ? (
                    <div className="text-muted">Loading gage list...</div>
                  ) : gageList.length === 0 ? (
                    <div className="text-muted">
                      <i className="ri-information-line me-1" />
                      No gages exist in the dashboard database yet. If the
                      legacy application shows gages, run
                      sql/Round60_Seed_MafiaGage_GaugeLocator.sql, then
                      Refresh List.
                    </div>
                  ) : (
                    <Row className="g-3 align-items-end">
                      <Col xl={9}>
                        <Label className="form-label">Select Gage</Label>
                        <Input
                          type="select"
                          value={selectedGage}
                          onChange={(e) => setSelectedGage(e.target.value)}
                        >
                          <option value="">-- Select Gage --</option>
                          {gageList.map((gage) => (
                            <option key={gage} value={gage}>
                              {gage}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col xl={3}>
                        <Button
                          color="danger"
                          className="w-100 gages-action-btn"
                          onClick={handleDeleteGage}
                          disabled={submitting}
                        >
                          <i className="ri-delete-bin-6-line gages-btn-icon me-1" />
                          {submitting ? "Deleting..." : "Delete"}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {/* Gages round (Issue 4): Gauge Locator browse — wires the
                  previously-unused api/mafia/gauge-locator and
                  api/mafia/unique-gauge-types endpoints so the page loads
                  gage records on open. Read-only; local pluto_SSWDASH
                  GaugeLocator table; no schema change. */}
              <div className="gages-panel-card mt-4">
                <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
                  <div>
                    <h5 className="mb-1">Gauge Locator</h5>
                    <p className="text-muted mb-0">
                      Browse gauge locations, types, and pass/fail assets.
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge color="light" className="gages-soft-badge text-uppercase">
                      {locatorLoading
                        ? "Loading"
                        : `${filteredLocatorRows.length} of ${locatorRows.length} records`}
                    </Badge>
                    <Button
                      color="light"
                      className="gages-action-btn border"
                      onClick={loadLocator}
                      disabled={locatorLoading}
                    >
                      <i className="ri-refresh-line gages-btn-icon me-1" />
                      {locatorLoading ? "Refreshing..." : "Refresh"}
                    </Button>
                  </div>
                </div>

                {locatorError ? (
                  <div className="gages-error-banner mb-3">
                    <i className="ri-error-warning-line me-2" />
                    {locatorError}
                  </div>
                ) : null}

                <Row className="g-3 mb-3">
                  <Col md={7}>
                    <Label className="form-label">Search</Label>
                    <Input
                      type="text"
                      value={locatorSearch}
                      onChange={(e) => setLocatorSearch(e.target.value)}
                      placeholder="Filter by asset, description, or location"
                    />
                  </Col>
                  <Col md={5}>
                    <Label className="form-label">Type</Label>
                    <Input
                      type="select"
                      value={locatorType}
                      onChange={(e) => setLocatorType(e.target.value)}
                    >
                      <option value="">All Types</option>
                      {locatorTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                <div
                  className="border rounded"
                  style={{ maxHeight: 340, overflow: "auto" }}
                >
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead
                      className="table-light"
                      style={{ position: "sticky", top: 0, zIndex: 1 }}
                    >
                      <tr>
                        <th>Asset No</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Location</th>
                        <th>GO</th>
                        <th>NG</th>
                        <th className="text-end">Uses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locatorLoading ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            Loading gauge locator records...
                          </td>
                        </tr>
                      ) : filteredLocatorRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            {locatorRows.length === 0
                              ? "No gauge locator records in the dashboard database. If the legacy system has them, run sql/Round60_Seed_MafiaGage_GaugeLocator.sql and Refresh."
                              : "No records match the current filter."}
                          </td>
                        </tr>
                      ) : (
                        filteredLocatorRows.map((row, idx) => (
                          <tr key={idx}>
                            <td className="fw-semibold text-dark">
                              {formatLocatorValue(row?.AssetNo ?? row?.assetNo)}
                            </td>
                            <td>{formatLocatorValue(row?.Description ?? row?.description)}</td>
                            <td>{formatLocatorValue(row?.Type ?? row?.type)}</td>
                            <td>{formatLocatorValue(row?.Location ?? row?.location)}</td>
                            <td>{formatLocatorValue(row?.GO ?? row?.go)}</td>
                            <td>{formatLocatorValue(row?.NG ?? row?.ng)}</td>
                            <td className="text-end">
                              {formatLocatorValue(row?.NumberOfUses ?? row?.numberOfUses)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .gages-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .gages-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .gages-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .gages-soft-badge {
          border: 1px solid #e9ebec;
          padding: 0.55rem 0.85rem;
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          border-radius: 999px;
        }

        .gages-panel-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
          height: 100%;
        }

        .gages-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .gages-action-btn:disabled,
          .gages-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .gages-utility-btn {
            min-width: 110px;
          }

        .gages-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .gages-info-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid #e9ebec;
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #212529;
          background: #f8fafc;
        }

        .gages-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .gages-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .gages-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .gages-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #878a99;
          font-weight: 500;
        }

        .gages-error-banner {
          display: flex;
          align-items: center;
          padding: 0.8rem 0.95rem;
          border-radius: 0.8rem;
          border: 1px solid rgba(220, 53, 69, 0.18);
          background: rgba(220, 53, 69, 0.06);
          color: #842029;
          font-weight: 500;
        }

        .gages-mode-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
        }

        .gages-mode-card {
          width: 100%;
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
          text-align: left;
          transition: all 0.18s ease;
        }

        .gages-mode-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
        }

        .gages-mode-card.active {
          background: rgba(13, 110, 253, 0.08);
          border-color: rgba(13, 110, 253, 0.28);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .gages-mode-icon {
          width: 42px;
          height: 42px;
          border-radius: 0.85rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          flex-shrink: 0;
          background: #eff2f7;
          color: #212529;
        }

        .gages-mode-card-primary .gages-mode-icon {
          background: rgba(13, 110, 253, 0.12);
          color: #0d6efd;
        }

        .gages-mode-card-warning .gages-mode-icon {
          background: rgba(255, 193, 7, 0.18);
          color: #997404;
        }

        .gages-mode-card-danger .gages-mode-icon {
          background: rgba(220, 53, 69, 0.12);
          color: #dc3545;
        }

        .gages-mode-title {
          font-weight: 700;
          color: #212529;
          margin-bottom: 0.2rem;
        }

        .gages-mode-copy {
          color: #878a99;
          font-size: 0.92rem;
          line-height: 1.45;
        }

        @media (max-width: 991.98px) {
          .gages-mode-grid {
            grid-template-columns: 1fr;
          }

          .gages-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .gages-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function toTitle(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

async function getGageList() {
  const response = await api.get("mafia/gagelist");
  return response.data;
}

async function addGage(request) {
  const response = await api.post("mafia/add-gage", {
    gage: request.gage,
  });

  return response.data;
}

async function modifyGage(request) {
  const response = await api.post("mafia/modify-gage", {
    oldGage: request.oldGage,
    newGage: request.newGage,
  });

  return response.data;
}

async function deleteGage(request) {
  const response = await api.post("mafia/delete-gage", {
    gage: request.gage,
  });

  return response.data;
}

// Gages round (Issue 4): read-only GaugeLocator browse (existing backend
// endpoints; previously never called from the frontend).
async function getGaugeLocator() {
  const response = await api.get("mafia/gauge-locator");
  return response.data;
}

async function getUniqueGaugeTypes() {
  const response = await api.get("mafia/unique-gauge-types");
  return response.data;
}

function formatLocatorValue(value) {
  if (value == null) return "";
  return String(value);
}

// Controlled error extraction: prefer the backend's plain-text or
// { message } payload (e.g. the 403 "Insufficient Mafia Aux permission for
// this action.") over a generic fallback, so failures never blank the UI
// and unauthorized users see the reason.
function extractApiError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (error?.response?.status === 403) {
    return "You do not have permission to perform this action.";
  }
  return fallback;
}